import { HttpParams } from '@angular/common/http';
import {
	ApiKeyListItem,
	ApiKeyStatus,
	CreateApiKeyDTO,
	CreatedApiKey,
} from 'services/api-key/api-key.model';
import {
	Branch,
	BranchDetail,
	CreateBranchDTO,
	UpdateBranchDTO,
} from 'services/branch/branch.model';
import { Client, CreateClientDTO, UpdateClientDTO } from 'services/client/client.model';
import { CreateFileTypeDTO, FileType, UpdateFileTypeDTO } from 'services/file-type/file-type.model';
import { Product } from 'services/product/product.model';
import {
	CreateBillingTierDTO,
	CreateProductSetupDTO,
	ProductSetupApiKey,
	ProductSetupBilling,
	ProductSetupDetail,
	ProductSetupListItem,
	ProductSetupUser,
	ProductSetupUserOption,
	UpdateBillingTierDTO,
	UpdateProductSetupDTO,
	UpdateWebhookDTO,
} from 'services/product-setup/product-setup.model';
import { Role } from 'services/role/role.model';
import { AdminUserContext, CreateUserDTO, UpdateUserDTO, User } from 'services/user/user.model';
import { CreateVerticalDTO, UpdateVerticalDTO, Vertical } from 'services/vertical/vertical.model';
import { MockHttpError, MockRoute, mockRoute, newMockId, nowIso, paginate } from '../mock-api';
import { MOCK_AUDIT_USER, MOCK_HUB_ROLES } from '../shared/mock-context.const';
import {
	aiEnginesDb,
	ApiKeyRecord,
	apiKeysDb,
	branchesDb,
	ClientRecord,
	clientsDb,
	fileTypesDb,
	productSetupsDb,
	productsDb,
	setupStatus,
	UserRecord,
	usersDb,
	verticalsDb,
} from './context.const';

// ── Helpers ───────────────────────────────────────────────────────────────────

function findOr404<T extends { id: string }>(items: T[], id: string, label: string): T {
	const item = items.find((candidate) => candidate.id === id);
	if (!item)
		throw new MockHttpError(404, `${label} não encontrado(a)`, `Nenhum registro com id ${id}.`);
	return item;
}

function normalize(text: string): string {
	return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function matchesSearch(term: string | null, ...fields: (string | null | undefined)[]): boolean {
	if (!term) return true;
	const normalizedTerm = normalize(term);
	return fields.some((field) => !!field && normalize(field).includes(normalizedTerm));
}

function isTruthyParam(query: HttpParams, key: string): boolean {
	return query.get(key) === 'true';
}

/** Ordena por `sortBy`/`sortOrder` da query; aceita caminhos simples como `client.name` */
function sortByQuery<T extends object>(
	items: T[],
	query: HttpParams,
	fallback?: keyof T & string,
): T[] {
	const sortBy = query.get('sortBy') ?? fallback;
	if (!sortBy) return items;
	const direction = query.get('sortOrder') === 'desc' ? -1 : 1;
	const valueOf = (item: T): unknown =>
		sortBy
			.split('.')
			.reduce<unknown>(
				(value, key) =>
					value && typeof value === 'object' ? (value as Record<string, unknown>)[key] : undefined,
				item,
			);
	return [...items].sort((first, second) => {
		const firstValue = valueOf(first);
		const secondValue = valueOf(second);
		if (firstValue == null) return 1;
		if (secondValue == null) return -1;
		if (typeof firstValue === 'number' && typeof secondValue === 'number')
			return (firstValue - secondValue) * direction;
		return String(firstValue).localeCompare(String(secondValue), 'pt-BR') * direction;
	});
}

function audit() {
	return { updatedAt: nowIso(), updatedBy: MOCK_AUDIT_USER };
}

function roleById(roleId: string): Role {
	const role = Object.values(MOCK_HUB_ROLES).find((candidate) => candidate.id === roleId);
	if (!role) throw new MockHttpError(422, 'Perfil inválido', `Perfil ${roleId} não existe.`);
	return role;
}

// ── Product setup ─────────────────────────────────────────────────────────────

function refreshSetup(setup: ProductSetupDetail): ProductSetupDetail {
	setup.status = setupStatus(setup);
	setup.billings = setup.billingTiers;
	setup.users = setupUsers(setup.id);
	setup.apiKey = setupApiKey(setup.id);
	return setup;
}

function setupUsers(productSetupId: string): ProductSetupUser[] {
	return usersDb
		.filter(
			(record) =>
				record.status !== 'REMOVED' &&
				record.contexts.some((context) => context.productSetupId === productSetupId),
		)
		.map((record) => ({
			id: record.id,
			name: record.name,
			email: record.email,
			image: record.image,
			status: record.status,
			lockedAt: record.lockedAt,
			createdAt: record.createdAt,
			updatedAt: record.updatedAt,
			createdBy: record.createdBy.id,
			updatedBy: record.updatedBy.id,
			deletedAt: record.deletedAt ?? null,
			deletedBy: record.deletedBy?.id ?? null,
		}));
}

function setupApiKey(productSetupId: string): ProductSetupApiKey | null {
	const record = apiKeysDb.find(
		(candidate) => candidate.productSetupId === productSetupId && !candidate.deletedAt,
	);
	if (!record) return null;
	return {
		id: record.id,
		name: record.name,
		prefix: record.prefix,
		expiresAt: record.expiresAt,
		lastUsedAt: record.lastUsedAt,
		createdAt: record.createdAt,
		updatedAt: record.updatedAt,
		createdBy: record.createdBy?.id ?? '',
		updatedBy: record.updatedBy?.id ?? '',
		deletedAt: record.deletedAt,
	};
}

function toSetupListItem(setup: ProductSetupDetail): ProductSetupListItem {
	const client = clientsDb.find((candidate) => candidate.id === setup.client.id);
	const vertical = verticalsDb.find((candidate) => candidate.id === setup.vertical.id);
	return {
		id: setup.id,
		client: { id: setup.client.id, name: setup.client.name, logo: client?.logo ?? null },
		productCatalog: { id: setup.productCatalog.id, name: setup.productCatalog.name },
		vertical: vertical
			? { id: vertical.id, name: vertical.name, metadata: vertical.metadata }
			: null,
		status: setupStatus(setup),
		isEnabled: setup.isEnabled,
		hasExpirationDate: setup.hasExpirationDate,
		limitType: setup.limitType,
		expirationDate: setup.expirationDate,
		createdAt: setup.createdAt,
	};
}

function findSetup(id: string): ProductSetupDetail {
	return findOr404(productSetupsDb, id, 'Configuração de produto');
}

function buildBilling(dto: CreateBillingTierDTO): ProductSetupBilling {
	return {
		id: newMockId('bill'),
		pageInitial: dto.pageInitial,
		pageFinal: dto.pageFinal ?? null,
		baseValue: dto.baseValue,
		excessChargeValue: dto.excessChargeValue ?? null,
		createdAt: nowIso(),
		updatedAt: nowIso(),
		createdBy: MOCK_AUDIT_USER.id,
		updatedBy: MOCK_AUDIT_USER.id,
		aiEngine: findOr404(aiEnginesDb, dto.aiEngineId, 'Motor de IA'),
	};
}

const PRODUCT_SETUP_ROUTES: MockRoute[] = [
	mockRoute('GET', '/product-setup', ({ query }) => {
		const productCatalogId = query.get('productCatalogId');
		const items = productSetupsDb
			.filter((setup) => !productCatalogId || setup.productCatalogId === productCatalogId)
			.map(toSetupListItem);
		return paginate(sortByQuery(items, query, 'createdAt'), query);
	}),

	mockRoute<CreateProductSetupDTO>('POST', '/product-setup', ({ body }) => {
		const client = findOr404(clientsDb, body.clientId, 'Cliente');
		const product = findOr404(productsDb, body.productCatalogId, 'Produto');
		const vertical = body.verticalId
			? findOr404(verticalsDb, body.verticalId, 'Vertical')
			: verticalsDb[0];
		const duplicated = productSetupsDb.some(
			(setup) =>
				setup.client.id === client.id &&
				setup.productCatalogId === product.id &&
				setup.vertical.id === vertical.id,
		);
		if (duplicated) {
			throw new MockHttpError(
				409,
				'Configuração já existe',
				`${client.name} já possui ${product.name} na vertical ${vertical.name}.`,
			);
		}
		const createdAt = nowIso();
		const customPromptId = newMockId('cprompt');
		const billings = body.billings.map(buildBilling);
		const setup: ProductSetupDetail = {
			id: newMockId('ps'),
			productCatalogId: product.id,
			client: { id: client.id, name: client.name },
			productPromptTemplateId: body.productPromptTemplateId,
			productCustomPromptId: customPromptId,
			isEnabled: true,
			hasExpirationDate: body.hasExpirationDate,
			expirationDate: body.hasExpirationDate ? body.expirationDate : null,
			hasMonthlyLimit: body.hasMonthlyLimit,
			monthlyLimit: body.hasMonthlyLimit ? body.monthlyLimit : null,
			limitType: body.hasMonthlyLimit ? body.limitType : null,
			hasExcessCharge: body.hasExcessCharge,
			createdAt,
			createdBy: MOCK_AUDIT_USER,
			updatedAt: createdAt,
			updatedBy: MOCK_AUDIT_USER,
			productCatalog: product,
			users: [],
			apiKey: null,
			promptTemplate: [],
			customPrompts: [
				{
					id: customPromptId,
					description: body.customPrompt.description,
					version: 1,
					createdAt,
					updatedAt: createdAt,
				},
			],
			billings,
			billingTiers: billings,
			vertical: { id: vertical.id, name: vertical.name },
			templateInfo: { id: body.productPromptTemplateId, description: null, version: 1 },
			activePrompt: {
				id: customPromptId,
				version: 1,
				description: body.customPrompt.description,
				updatedAt: createdAt,
			},
			status: 'Ativo',
			webhookUrl: null,
			webhookHeaders: null,
			webhookResponseFormat: null,
		};
		productSetupsDb.push(setup);
		return refreshSetup(setup);
	}),

	mockRoute('GET', '/product-setup/:id', ({ params }) => refreshSetup(findSetup(params['id']))),

	mockRoute<UpdateProductSetupDTO>('PATCH', '/product-setup/:id', ({ params, body }) => {
		const setup = findSetup(params['id']);
		Object.assign(setup, body, audit());
		if (!setup.hasExpirationDate) setup.expirationDate = null;
		if (!setup.hasMonthlyLimit) {
			setup.monthlyLimit = null;
			setup.limitType = null;
		}
		return refreshSetup(setup);
	}),

	mockRoute('GET', '/product-setup/:id/users', ({ params }): ProductSetupUserOption[] => {
		findSetup(params['id']);
		return setupUsers(params['id']).map(({ id, name, email, image }) => ({
			id,
			name,
			email,
			image,
		}));
	}),

	mockRoute<UpdateWebhookDTO>('PATCH', '/product-setup/:id/webhook', ({ params, body }) => {
		const setup = findSetup(params['id']);
		if (body.webhookUrl !== undefined) setup.webhookUrl = body.webhookUrl;
		if (body.webhookHeaders !== undefined) setup.webhookHeaders = body.webhookHeaders;
		if (body.webhookResponseFormat !== undefined)
			setup.webhookResponseFormat = body.webhookResponseFormat;
		Object.assign(setup, audit());
		return refreshSetup(setup);
	}),

	mockRoute<CreateBillingTierDTO>('POST', '/product-setup/:id/billing-tier', ({ params, body }) => {
		const setup = findSetup(params['id']);
		const billing = buildBilling(body);
		setup.billingTiers = [...setup.billingTiers, billing];
		refreshSetup(setup);
		return billing;
	}),

	mockRoute<UpdateBillingTierDTO>(
		'PATCH',
		'/product-setup/:id/billing-tier/:tierId',
		({ params, body }) => {
			const setup = findSetup(params['id']);
			const billing = findOr404(setup.billingTiers, params['tierId'], 'Faixa de faturamento');
			const { aiEngineId, ...rest } = body;
			Object.assign(billing, rest, { updatedAt: nowIso(), updatedBy: MOCK_AUDIT_USER.id });
			if (aiEngineId) billing.aiEngine = findOr404(aiEnginesDb, aiEngineId, 'Motor de IA');
			return billing;
		},
	),

	mockRoute('DELETE', '/product-setup/:id/billing-tier/:tierId', ({ params }) => {
		const setup = findSetup(params['id']);
		findOr404(setup.billingTiers, params['tierId'], 'Faixa de faturamento');
		setup.billingTiers = setup.billingTiers.filter((billing) => billing.id !== params['tierId']);
		refreshSetup(setup);
		return null;
	}),
];

// ── API keys ──────────────────────────────────────────────────────────────────

const EXPIRING_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

function apiKeyStatus(record: ApiKeyRecord): ApiKeyStatus {
	if (record.deletedAt) return 'REVOKED';
	if (!record.expiresAt) return 'ACTIVE';
	const remaining = new Date(record.expiresAt).getTime() - Date.now();
	if (remaining < 0) return 'EXPIRED';
	return remaining <= EXPIRING_WINDOW_MS ? 'EXPIRING' : 'ACTIVE';
}

function toApiKey(record: ApiKeyRecord): ApiKeyListItem {
	const setup = productSetupsDb.find((candidate) => candidate.id === record.productSetupId);
	const vertical = setup
		? verticalsDb.find((candidate) => candidate.id === setup.vertical.id)
		: undefined;
	return {
		...record,
		product: setup
			? {
					id: setup.id,
					client: { ...setup.client },
					vertical: vertical
						? { id: vertical.id, name: vertical.name, metadata: { ...vertical.metadata } }
						: null,
					catalog: { id: setup.productCatalog.id, name: setup.productCatalog.name, metadata: {} },
				}
			: null,
	};
}

function randomSecret(length: number): string {
	const alphabet = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
	return Array.from({ length }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join(
		'',
	);
}

const API_KEY_ROUTES: MockRoute[] = [
	mockRoute('GET', '/api-key', ({ query }) => {
		const productSetupId = query.get('productSetupId');
		const clientId = query.get('clientId');
		const status = query.get('status');
		const name = query.get('name');
		const items = apiKeysDb
			.filter((record) => !productSetupId || record.productSetupId === productSetupId)
			.filter((record) => !status || apiKeyStatus(record) === status)
			.filter((record) => matchesSearch(name, record.name, record.prefix))
			.map(toApiKey)
			.filter((item) => !clientId || item.product?.client.id === clientId);
		return paginate(sortByQuery(items, query, 'createdAt'), query);
	}),

	mockRoute<CreateApiKeyDTO>('POST', '/api-key', ({ body }): CreatedApiKey => {
		findSetup(body.productId);
		const createdAt = nowIso();
		const prefix = `lbca_live_${randomSecret(4).toLowerCase()}`;
		const record: ApiKeyRecord = {
			id: newMockId('key'),
			name: body.name,
			prefix,
			productSetupId: body.productId,
			expiresAt: body.expiresAt ?? null,
			lastUsedAt: null,
			createdAt,
			createdBy: MOCK_AUDIT_USER,
			updatedAt: createdAt,
			updatedBy: MOCK_AUDIT_USER,
			deletedAt: null,
			deletedBy: null,
		};
		apiKeysDb.unshift(record);
		return {
			id: record.id,
			name: record.name,
			prefix,
			expiresAt: record.expiresAt,
			createdAt,
			plainKey: `${prefix}_${randomSecret(40)}`,
		};
	}),

	mockRoute('GET', '/api-key/:id', ({ params }) =>
		toApiKey(findOr404(apiKeysDb, params['id'], 'Chave de API')),
	),

	mockRoute('POST', '/api-key/:id/revoke', ({ params }) => {
		const record = findOr404(apiKeysDb, params['id'], 'Chave de API');
		if (record.deletedAt)
			throw new MockHttpError(409, 'Chave já revogada', 'Esta chave de API já foi revogada.');
		Object.assign(record, audit(), { deletedAt: nowIso(), deletedBy: MOCK_AUDIT_USER });
		return null;
	}),
];

// ── File types ────────────────────────────────────────────────────────────────

function findFileType(id: string, includeDeleted: boolean): FileType {
	const fileType = findOr404(fileTypesDb, id, 'Tipo de arquivo');
	if (fileType.deletedAt && !includeDeleted)
		throw new MockHttpError(404, 'Tipo de arquivo não encontrado');
	return fileType;
}

function assertUniqueName<T extends { id: string; name: string }>(
	items: T[],
	name: string | undefined,
	label: string,
	ignoreId?: string,
) {
	if (!name) return;
	const duplicated = items.some(
		(item) => item.id !== ignoreId && normalize(item.name) === normalize(name),
	);
	if (duplicated)
		throw new MockHttpError(
			409,
			`${label} já existe`,
			`Já existe um registro com o nome "${name}".`,
		);
}

const FILE_TYPE_ROUTES: MockRoute[] = [
	mockRoute('GET', '/file-type', ({ query }) => {
		const includeDeleted = isTruthyParam(query, 'includeDeleted');
		const search = query.get('search');
		const items = fileTypesDb
			.filter((fileType) => includeDeleted || !fileType.deletedAt)
			.filter((fileType) => matchesSearch(search, fileType.name, fileType.description));
		const sortBy = query.get('sortBy');
		return paginate(sortBy === 'usage' ? items : sortByQuery(items, query, 'name'), query);
	}),

	mockRoute<CreateFileTypeDTO>('POST', '/file-type', ({ body }) => {
		assertUniqueName(fileTypesDb, body.name, 'Tipo de arquivo');
		const fileType: FileType = {
			id: newMockId('ftype'),
			name: body.name,
			description: body.description ?? null,
			createdAt: nowIso(),
			createdBy: MOCK_AUDIT_USER,
			...audit(),
			deletedAt: null,
			deletedBy: null,
		};
		fileTypesDb.push(fileType);
		return fileType;
	}),

	mockRoute('GET', '/file-type/:id', ({ params, query }) =>
		findFileType(params['id'], isTruthyParam(query, 'includeDeleted')),
	),

	mockRoute<UpdateFileTypeDTO>('PATCH', '/file-type/:id', ({ params, body }) => {
		const fileType = findFileType(params['id'], false);
		assertUniqueName(fileTypesDb, body.name, 'Tipo de arquivo', fileType.id);
		return Object.assign(fileType, body, audit());
	}),

	mockRoute('DELETE', '/file-type/:id', ({ params }) => {
		const fileType = findFileType(params['id'], false);
		Object.assign(fileType, audit(), { deletedAt: nowIso(), deletedBy: MOCK_AUDIT_USER });
		return null;
	}),

	mockRoute('POST', '/file-type/:id/restore', ({ params }) => {
		const fileType = findFileType(params['id'], true);
		return Object.assign(fileType, audit(), { deletedAt: null, deletedBy: null });
	}),
];

// ── Users ─────────────────────────────────────────────────────────────────────

function toAdminContext(productSetupId: string, roleId: string): AdminUserContext | null {
	const setup = productSetupsDb.find((candidate) => candidate.id === productSetupId);
	if (!setup) return null;
	const client = clientsDb.find((candidate) => candidate.id === setup.client.id);
	return {
		productSetup: {
			id: setup.id,
			name: setup.productCatalog.name,
			description: setup.productCatalog.description,
			client: { id: setup.client.id, name: setup.client.name, logo: client?.logo ?? null },
			vertical: { id: setup.vertical.id, name: setup.vertical.name },
			createdAt: setup.createdAt,
			updatedAt: setup.updatedAt,
		},
		role: roleById(roleId),
	};
}

function toUser(record: UserRecord): User {
	return {
		...record,
		contexts: record.contexts
			.map((context) => toAdminContext(context.productSetupId, context.roleId))
			.filter((context): context is AdminUserContext => context !== null),
	};
}

function findUser(id: string): UserRecord {
	return findOr404(usersDb, id, 'Usuário');
}

function validateContexts(userContexts: CreateUserDTO['contexts']) {
	for (const context of userContexts) {
		findSetup(context.productSetupId);
		roleById(context.roleId);
	}
	return userContexts.map(({ productSetupId, roleId }) => ({ productSetupId, roleId }));
}

const USER_ROUTES: MockRoute[] = [
	mockRoute('GET', '/user', ({ query }) => {
		const productSetupId = query.get('productSetupId');
		const status = query.get('status');
		const includeDeleted = isTruthyParam(query, 'includeDeleted') || status === 'REMOVED';
		const name = query.get('name');
		const email = query.get('email');
		const items = usersDb
			.filter((record) => includeDeleted || record.status !== 'REMOVED')
			.filter((record) => !status || record.status === status)
			.filter(
				(record) =>
					!productSetupId ||
					record.contexts.some((context) => context.productSetupId === productSetupId),
			)
			.filter((record) => matchesSearch(name, record.name, record.email))
			.filter((record) => matchesSearch(email, record.email))
			.map(toUser);
		return paginate(sortByQuery(items, query, 'name'), query);
	}),

	mockRoute<CreateUserDTO>('POST', '/user', ({ body }) => {
		const email = body.email.trim().toLowerCase();
		const existing = usersDb.find((record) => record.email.toLowerCase() === email);
		if (existing) {
			throw new MockHttpError(
				409,
				'E-mail já cadastrado',
				`Já existe um usuário com o e-mail ${email}.`,
			);
		}
		const createdAt = nowIso();
		const record: UserRecord = {
			id: newMockId('usr'),
			name: body.name,
			email,
			image: body.image ?? null,
			lockedAt: null,
			status: 'INACTIVE',
			isAdmin: false,
			createdAt,
			createdBy: MOCK_AUDIT_USER,
			updatedAt: createdAt,
			updatedBy: MOCK_AUDIT_USER,
			contexts: validateContexts(body.contexts),
		};
		usersDb.push(record);
		return toUser(record);
	}),

	mockRoute('GET', '/user/:id', ({ params }) => toUser(findUser(params['id']))),

	mockRoute<UpdateUserDTO>('PATCH', '/user/:id', ({ params, body }) => {
		const record = findUser(params['id']);
		const { contexts, ...rest } = body;
		Object.assign(record, rest, audit());
		if (contexts) record.contexts = validateContexts(contexts);
		return toUser(record);
	}),

	mockRoute('DELETE', '/user/:id', ({ params }) => {
		const record = findUser(params['id']);
		Object.assign(record, audit(), {
			status: 'REMOVED',
			deletedAt: nowIso(),
			deletedBy: MOCK_AUDIT_USER,
		});
		return null;
	}),

	mockRoute('POST', '/user/:id/resend-invite', ({ params }) => {
		const record = findUser(params['id']);
		if (record.status !== 'INACTIVE') {
			throw new MockHttpError(
				422,
				'Convite não pode ser reenviado',
				'O usuário já aceitou o convite.',
			);
		}
		Object.assign(record, audit());
		return null;
	}),

	mockRoute('POST', '/user/:id/reactivate', ({ params }) => {
		const record = findUser(params['id']);
		Object.assign(record, audit(), {
			status: 'ACTIVE',
			deletedAt: undefined,
			deletedBy: undefined,
		});
		return toUser(record);
	}),

	mockRoute<{ isAdmin: boolean }>('PATCH', '/user/:id/admin/manage', ({ params, body }) => {
		const record = findUser(params['id']);
		Object.assign(record, audit(), { isAdmin: body.isAdmin });
		return toUser(record);
	}),

	mockRoute('POST', '/user/:id/unlock', ({ params }) => {
		const record = findUser(params['id']);
		Object.assign(record, audit(), {
			lockedAt: null,
			status: record.status === 'BLOCKED' ? 'ACTIVE' : record.status,
		});
		return toUser(record);
	}),

	// Como no backend: sai só o vínculo com o produto da query, e a conta segue ativa.
	mockRoute('POST', '/user/:id/revoke-access', ({ params, query }) => {
		const record = findUser(params['id']);
		const productSetupId = query.get('productSetupId');
		const remaining = record.contexts.filter(
			(context) => context.productSetupId !== productSetupId,
		);
		if (remaining.length === record.contexts.length) {
			throw new MockHttpError(404, 'Usuário não encontrado');
		}
		record.contexts = remaining;
		Object.assign(record, audit());
		return null;
	}),
];

// ── Product catalog ───────────────────────────────────────────────────────────

const PRODUCT_ROUTES: MockRoute[] = [
	mockRoute('GET', '/product-catalog', ({ query }) => {
		const name = query.get('name');
		const description = query.get('description');
		const items = productsDb
			.filter((product) => matchesSearch(name, product.name))
			.filter((product) => matchesSearch(description, product.description));
		return paginate(sortByQuery(items, query, 'name'), query);
	}),

	mockRoute('GET', '/product-catalog/:id', ({ params }) =>
		findOr404(productsDb, params['id'], 'Produto'),
	),

	mockRoute<Partial<Pick<Product, 'name' | 'description'>>>(
		'PATCH',
		'/product-catalog/:id',
		({ params, body }) => {
			const product = findOr404(productsDb, params['id'], 'Produto');
			Object.assign(product, body, audit());
			for (const setup of productSetupsDb) {
				if (setup.productCatalogId === product.id) setup.productCatalog = product;
			}
			return product;
		},
	),
];

// ── Clients ───────────────────────────────────────────────────────────────────

function toClient(record: ClientRecord): Client {
	return {
		...record,
		productSetups: productSetupsDb
			.filter((setup) => setup.client.id === record.id)
			.map(refreshSetup),
	};
}

function findBranch(id: string): Branch {
	return findOr404(branchesDb, id, 'Ramo');
}

const CLIENT_ROUTES: MockRoute[] = [
	mockRoute('GET', '/client', ({ query }) => {
		const search = query.get('search');
		const branchIds = query.getAll('branchIds') ?? [];
		const verticalId = query.get('verticalId');
		const includeDeleted = isTruthyParam(query, 'includeDeleted');
		const items = clientsDb
			.filter((record) => includeDeleted || !record.deletedAt)
			.filter((record) => matchesSearch(search, record.name, record.cnpj))
			.filter((record) => branchIds.length === 0 || branchIds.includes(record.branch.id))
			.filter(
				(record) =>
					!verticalId ||
					productSetupsDb.some(
						(setup) => setup.client.id === record.id && setup.vertical.id === verticalId,
					),
			)
			.map(toClient);
		return paginate(sortByQuery(items, query, 'name'), query);
	}),

	mockRoute<CreateClientDTO>('POST', '/client', ({ body }) => {
		const cnpj = body.cnpj.replace(/\D/g, '');
		if (clientsDb.some((record) => record.cnpj === cnpj)) {
			throw new MockHttpError(
				409,
				'CNPJ já cadastrado',
				`Já existe um cliente com o CNPJ ${cnpj}.`,
			);
		}
		const createdAt = nowIso();
		const record: ClientRecord = {
			id: newMockId('cli'),
			name: body.name,
			cnpj,
			logo: body.logo ?? null,
			createdAt,
			createdBy: MOCK_AUDIT_USER,
			updatedAt: createdAt,
			updatedBy: MOCK_AUDIT_USER,
			deletedAt: null,
			deletedBy: null,
			branch: findBranch(body.branchId),
		};
		clientsDb.push(record);
		return toClient(record);
	}),

	mockRoute('GET', '/client/:id', ({ params }) =>
		toClient(findOr404(clientsDb, params['id'], 'Cliente')),
	),

	mockRoute<UpdateClientDTO>('PATCH', '/client/:id', ({ params, body }) => {
		const record = findOr404(clientsDb, params['id'], 'Cliente');
		const { branchId, ...rest } = body;
		Object.assign(record, rest, audit());
		if (branchId) record.branch = findBranch(branchId);
		for (const setup of productSetupsDb) {
			if (setup.client.id === record.id) setup.client = { id: record.id, name: record.name };
		}
		return toClient(record);
	}),

	mockRoute('DELETE', '/client/:id', ({ params }) => {
		const record = findOr404(clientsDb, params['id'], 'Cliente');
		Object.assign(record, audit(), { deletedAt: nowIso(), deletedBy: MOCK_AUDIT_USER });
		return null;
	}),

	mockRoute('POST', '/client/:id/reactivate', ({ params }) => {
		const record = findOr404(clientsDb, params['id'], 'Cliente');
		Object.assign(record, audit(), { deletedAt: null, deletedBy: null });
		return toClient(record);
	}),
];

// ── Verticals ─────────────────────────────────────────────────────────────────

function slugify(text: string): string {
	return normalize(text)
		.replace(/[^a-z0-9]+/g, '-')
		.replace(/(^-|-$)/g, '');
}

function findVertical(id: string, includeDeleted: boolean): Vertical {
	const vertical = findOr404(verticalsDb, id, 'Vertical');
	if (vertical.deletedAt && !includeDeleted)
		throw new MockHttpError(404, 'Vertical não encontrada');
	return vertical;
}

const VERTICAL_ROUTES: MockRoute[] = [
	mockRoute('GET', '/vertical', ({ query }) => {
		const status = query.get('status') ?? 'ACTIVE';
		const name = query.get('name');
		const items = verticalsDb
			.filter(
				(vertical) =>
					status === 'ALL' || (status === 'DELETED' ? !!vertical.deletedAt : !vertical.deletedAt),
			)
			.filter((vertical) => matchesSearch(name, vertical.name));
		return paginate(sortByQuery(items, query, 'name'), query);
	}),

	mockRoute<CreateVerticalDTO>('POST', '/vertical', ({ body }) => {
		assertUniqueName(verticalsDb, body.name, 'Vertical');
		const createdAt = nowIso();
		const vertical: Vertical = {
			id: newMockId('vert'),
			name: body.name,
			slug: slugify(body.name),
			description: body.description ?? '',
			metadata: body.metadata,
			createdAt,
			createdBy: MOCK_AUDIT_USER,
			updatedAt: createdAt,
			updatedBy: MOCK_AUDIT_USER,
			deletedAt: null,
			deletedBy: null,
		};
		verticalsDb.push(vertical);
		return vertical;
	}),

	mockRoute('GET', '/vertical/:id', ({ params, query }) =>
		findVertical(params['id'], isTruthyParam(query, 'includeDeleted')),
	),

	mockRoute<UpdateVerticalDTO>('PATCH', '/vertical/:id', ({ params, body }) => {
		const vertical = findVertical(params['id'], false);
		assertUniqueName(verticalsDb, body.name, 'Vertical', vertical.id);
		Object.assign(vertical, body, audit());
		for (const setup of productSetupsDb) {
			if (setup.vertical.id === vertical.id)
				setup.vertical = { id: vertical.id, name: vertical.name };
		}
		return vertical;
	}),

	mockRoute('DELETE', '/vertical/:id', ({ params }) => {
		const vertical = findVertical(params['id'], false);
		if (productSetupsDb.some((setup) => setup.vertical.id === vertical.id)) {
			throw new MockHttpError(
				409,
				'Vertical em uso',
				'Existem produtos configurados nesta vertical.',
			);
		}
		Object.assign(vertical, audit(), { deletedAt: nowIso(), deletedBy: MOCK_AUDIT_USER });
		return null;
	}),

	mockRoute('POST', '/vertical/:id/restore', ({ params }) => {
		const vertical = findVertical(params['id'], true);
		return Object.assign(vertical, audit(), { deletedAt: null, deletedBy: null });
	}),
];

// ── Branches ──────────────────────────────────────────────────────────────────

function findActiveBranch(id: string, includeDeleted: boolean): Branch {
	const record = findBranch(id);
	if (record.deletedAt && !includeDeleted) throw new MockHttpError(404, 'Ramo não encontrado');
	return record;
}

function toBranchDetail(record: Branch): BranchDetail {
	const clients = clientsDb
		.filter((client) => client.branch.id === record.id && !client.deletedAt)
		.map((client) => ({ id: client.id, name: client.name, logo: client.logo }));
	return {
		...record,
		clients,
		clientsTotal: clients.length,
		productPromptTemplatesCount: 0,
		createdById: record.createdBy.id,
		updatedById: record.updatedBy.id,
	};
}

const BRANCH_ROUTES: MockRoute[] = [
	mockRoute('GET', '/branch', ({ query }) => {
		const includeDeleted = isTruthyParam(query, 'includeDeleted');
		const name = query.get('name');
		const items = branchesDb
			.filter((record) => includeDeleted || !record.deletedAt)
			.filter((record) => matchesSearch(name, record.name));
		return paginate(sortByQuery(items, query, 'name'), query);
	}),

	mockRoute<CreateBranchDTO>('POST', '/branch', ({ body }) => {
		assertUniqueName(branchesDb, body.name, 'Ramo');
		const createdAt = nowIso();
		const record: Branch = {
			id: newMockId('branch'),
			name: body.name,
			description: body.description ?? null,
			createdAt,
			createdBy: MOCK_AUDIT_USER,
			updatedAt: createdAt,
			updatedBy: MOCK_AUDIT_USER,
			deletedAt: null,
			deletedBy: null,
		};
		branchesDb.push(record);
		return record;
	}),

	mockRoute('GET', '/branch/:id', ({ params, query }) =>
		toBranchDetail(findActiveBranch(params['id'], isTruthyParam(query, 'includeDeleted'))),
	),

	mockRoute<UpdateBranchDTO>('PATCH', '/branch/:id', ({ params, body }) => {
		const record = findActiveBranch(params['id'], false);
		assertUniqueName(branchesDb, body.name, 'Ramo', record.id);
		return Object.assign(record, body, audit());
	}),

	mockRoute('DELETE', '/branch/:id', ({ params }) => {
		const record = findActiveBranch(params['id'], false);
		if (clientsDb.some((client) => client.branch.id === record.id && !client.deletedAt)) {
			throw new MockHttpError(409, 'Ramo em uso', 'Existem clientes vinculados a este ramo.');
		}
		Object.assign(record, audit(), { deletedAt: nowIso(), deletedBy: MOCK_AUDIT_USER });
		return null;
	}),

	mockRoute('POST', '/branch/:id/restore', ({ params }) => {
		const record = findActiveBranch(params['id'], true);
		return Object.assign(record, audit(), { deletedAt: null, deletedBy: null });
	}),
];

/** Rotas de contexto/admin: configurações de produto, chaves de API, tipos de arquivo, usuários e cadastros base */
export const CONTEXT_MOCK_ROUTES: MockRoute[] = [
	...PRODUCT_SETUP_ROUTES,
	...API_KEY_ROUTES,
	...FILE_TYPE_ROUTES,
	...USER_ROUTES,
	...PRODUCT_ROUTES,
	...CLIENT_ROUTES,
	...VERTICAL_ROUTES,
	...BRANCH_ROUTES,
];
